import React from "react";
import {
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import classes from "./TrendLineChart.module.css";

function formatNumber(value) {
  if (value == null || Number.isNaN(Number(value))) return "";
  return Number(value).toLocaleString();
}

function formatShortNumber(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  const abs = Math.abs(number);

  if (abs >= 1_000_000) {
    return `${Number.parseFloat((number / 1_000_000).toFixed(1))}M`;
  }

  if (abs >= 1_000) {
    return `${Number.parseFloat((number / 1_000).toFixed(1))}K`;
  }

  return String(number);
}

function parseDateish(value) {
  if (!value) return null;
  const text = String(value);
  const isoMatch = text.match(/^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?/);

  if (isoMatch) {
    return new Date(
      Date.UTC(
        Number(isoMatch[1]),
        Number(isoMatch[2] || 1) - 1,
        Number(isoMatch[3] || 1),
      ),
    );
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatTickDate(date, options) {
  return date.toLocaleString("en-US", {
    ...options,
    timeZone: "UTC",
  });
}

function formatXAxisLabel(point, granularity, xKey) {
  const normalizedGranularity = String(granularity || "").toLowerCase();
  const rawValue =
    point?.period || point?.reporting_date || point?.[xKey] || point?.label;
  const date = parseDateish(rawValue);

  if (normalizedGranularity.includes("annual")) {
    if (date) return formatTickDate(date, { year: "numeric" });
    const yearMatch = String(rawValue || "").match(/\b\d{4}\b/);
    return yearMatch ? yearMatch[0] : String(point?.[xKey] || "");
  }

  if (
    normalizedGranularity.includes("round") ||
    normalizedGranularity.includes("reported")
  ) {
    if (date) {
      return formatTickDate(date, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    }
    return String(point?.[xKey] || "");
  }

  if (date) {
    return formatTickDate(date, {
      month: "short",
      year: "numeric",
    });
  }

  return String(point?.[xKey] || "");
}

function getMaxFourTicks(data, xKey) {
  if (!Array.isArray(data) || data.length <= 4) {
    return data.map((point) => point[xKey]);
  }

  const lastIndex = data.length - 1;
  return [0, 1, 2, 3].map((index) => {
    const dataIndex = Math.round((index * lastIndex) / 3);
    return data[dataIndex][xKey];
  });
}

function getYDomain(data, yKey) {
  const values = data
    .map((point) => Number(point?.[yKey]))
    .filter(Number.isFinite);

  if (!values.length) return ["auto", "auto"];

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const padding = span > 0 ? span * 0.08 : Math.max(Math.abs(max) * 0.08, 1);

  return [Math.max(0, min - padding), max + padding];
}

function findAnnotationX(annotation, data, xKey) {
  if (!annotation || !Array.isArray(data)) return null;

  const match = data.find((point) => {
    return (
      point.period === annotation.period ||
      point.label === annotation.period_label ||
      point.label === annotation.label ||
      point[xKey] === annotation.period ||
      point[xKey] === annotation.period_label ||
      point[xKey] === annotation.label
    );
  });

  return match ? match[xKey] : null;
}

function isAnnotationPoint(annotation, point, yKey) {
  if (!annotation || !point) return false;

  const hasPeriodIdentity =
    annotation.period != null ||
    annotation.period_label != null ||
    annotation.label != null;

  if (hasPeriodIdentity) {
    return (
      point.period === annotation.period ||
      point.label === annotation.period_label ||
      point.label === annotation.label ||
      point.__xLabel === annotation.period_label ||
      point.__xLabel === annotation.label
    );
  }

  return Number(point?.[yKey]) === Number(annotation.value);
}

function AnnotationDot({ annotation, color, data, xKey }) {
  if (!annotation || annotation.value == null) return null;

  const x = findAnnotationX(annotation, data, xKey);
  if (x == null) return null;

  return (
    <ReferenceDot
      x={x}
      y={annotation.value}
      r={5.75}
      fill={color}
      stroke="#ffffff"
      strokeWidth={2}
    />
  );
}

function AnnotationLine({ annotation, color, data, xKey }) {
  const x = findAnnotationX(annotation, data, xKey);
  if (x == null) return null;

  return (
    <ReferenceLine
      x={x}
      stroke={color}
      strokeWidth={1.5}
      strokeDasharray="3 3"
    />
  );
}

function TrendTooltip({ active, label, labelByX, metricLabel, payload }) {
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload || {};
  const annotationType = point.__annotationType;
  const period = point.__xLabel || labelByX.get(label) || label;
  const value = payload[0]?.value;

  return (
    <div className={classes.tooltip}>
      {annotationType && (
        <div className={classes.tooltipTitle}>{annotationType}</div>
      )}
      <div className={classes.tooltipPeriod}>{period}</div>
      <div className={classes.tooltipValue}>
        <span>{metricLabel}</span>
        <strong>{formatNumber(value)}</strong>
      </div>
    </div>
  );
}

function TrendActiveDot({ cx, cy, payload }) {
  const annotationType = payload?.__annotationType;
  const fill =
    annotationType === "Highest"
      ? "#f97316"
      : annotationType === "Lowest"
        ? "#22a06b"
        : "#173ea5";

  return (
    <circle
      cx={cx}
      cy={cy}
      r={annotationType ? 6.6 : 6}
      fill={fill}
      stroke="#ffffff"
      strokeWidth={2}
    />
  );
}

function normalizeAnnotations(annotations) {
  if (!annotations) return {};
  if (!Array.isArray(annotations)) return annotations;

  return annotations.reduce((acc, annotation) => {
    if (annotation?.key) {
      acc[annotation.key] = annotation;
    }
    return acc;
  }, {});
}

export default function TrendLineChart({ chart }) {
  const series = chart?.series?.[0];
  const data = Array.isArray(chart?.data)
    ? chart.data
    : Array.isArray(series?.data)
      ? series.data
      : [];

  if (!chart || data.length < 2) {
    return null;
  }

  const title = chart.title;
  const xKey = chart.xKey || "label";
  const yKey = chart.yKey || "value";
  const granularity = chart.granularity || chart.response_type_analysis?.granularity;
  const metricLabel =
    chart.metricLabel || chart.metric?.label || series?.label || "Value";
  const annotations = normalizeAnnotations(chart.annotations);
  const data_policy = chart.data_policy;
  const displayData = data.map((point, index) => {
    const annotationType = isAnnotationPoint(annotations.peak, point, yKey)
      ? "Highest"
      : isAnnotationPoint(annotations.valley, point, yKey)
        ? "Lowest"
        : "";

    return {
      ...point,
      __annotationType: annotationType,
      __x: `${point?.[xKey] ?? point?.label ?? point?.period ?? index}-${index}`,
      __xLabel: formatXAxisLabel(point, granularity, xKey),
    };
  });
  const tickLabelByX = new Map(
    displayData.map((point) => [point.__x, point.__xLabel]),
  );
  const xTicks = getMaxFourTicks(displayData, "__x");
  const yDomain = getYDomain(displayData, yKey);

  return (
    <div className={classes.card}>
      <div className={classes.header}>
        <h4>{title || "Trend chart"}</h4>
      </div>

      <ResponsiveContainer width="100%" height={216}>
        <LineChart
          data={displayData}
          margin={{ top: 28, right: 24, left: 0, bottom: 8 }}
        >
          <XAxis
            dataKey="__x"
            tick={{ fontSize: 11 }}
            ticks={xTicks}
            tickFormatter={(value) => tickLabelByX.get(value) || ""}
          />
          <YAxis
            width={58}
            tick={{ fontSize: 11 }}
            domain={yDomain}
            tickFormatter={formatShortNumber}
          />
          <Tooltip
            content={
              <TrendTooltip
                labelByX={tickLabelByX}
                metricLabel={metricLabel}
              />
            }
          />

          {annotations.average?.value != null && (
            <ReferenceLine
              y={annotations.average.value}
              stroke="#64748b"
              strokeWidth={4.5}
              strokeDasharray="4 4"
              label={{
                value: "Average",
                position: "insideTopRight",
                fontSize: 11,
                fill: "#64748b",
              }}
            />
          )}

          <AnnotationLine
            annotation={annotations.peak}
            color="#f97316"
            data={displayData}
            xKey="__x"
          />
          <AnnotationLine
            annotation={annotations.valley}
            color="#22a06b"
            data={displayData}
            xKey="__x"
          />

          <Line
            type="monotone"
            dataKey={yKey}
            name={metricLabel}
            stroke="#173ea5"
            strokeWidth={2.5}
            dot={false}
            activeDot={<TrendActiveDot />}
          />

          <AnnotationDot
            annotation={annotations.peak}
            color="#f97316"
            data={displayData}
            xKey="__x"
          />
          <AnnotationDot
            annotation={annotations.valley}
            color="#22a06b"
            data={displayData}
            xKey="__x"
          />
        </LineChart>
      </ResponsiveContainer>

      {data_policy?.downsampled && (
        <p className={classes.note}>
          Chart simplified for display; analysis uses the full series.
        </p>
      )}
    </div>
  );
}
